import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Upload, FileSpreadsheet, CheckCircle, XCircle, Loader2,
  AlertTriangle, Download, File, Trash2,
} from 'lucide-react';

export default function ImportModal({ open, onClose, onImport, onSuccess, title = 'Import', acceptTypes = '.xlsx,.xls,.csv', templateUrl }) {
  const [step, setStep] = useState('select');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef();

  const reset = useCallback(() => {
    setStep('select');
    setFile(null);
    setProgress(0);
    setResult(null);
    setDragOver(false);
  }, []);

  const handleClose = () => {
    const wasSuccess = step === 'result' && result && !result.error && result.status !== 'failed';
    reset();
    onClose();
    // Refresh after modal is closed — use setTimeout to ensure state is clean
    if (wasSuccess) setTimeout(() => onSuccess?.(), 100);
  };

  const handleFileSelect = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setResult({ error: 'Invalid file type. Please upload .xlsx, .xls, or .csv file.' });
      setStep('result');
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer?.files?.[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    setStep('uploading');
    setProgress(0);
    const interval = setInterval(() => {
      setProgress(prev => prev >= 90 ? (clearInterval(interval), 90) : prev + Math.random() * 12);
    }, 400);
    try {
      const res = await onImport(file);
      clearInterval(interval);
      setProgress(100);
      // Check if the service returned an error response (uploadService catches and returns errors)
      if (res && (res.error || res.status === 'failed')) {
        setResult({ error: res.error || res.message || 'Import failed.', ...res });
      } else if (res && typeof res === 'object' && (res.status || res.message || res.successCount !== undefined)) {
        setResult(res);
      } else {
        setResult({ status: 'success', message: 'Import completed successfully.', ...res });
      }
      setStep('result');
    } catch (err) {
      clearInterval(interval);
      setProgress(100);
      setResult({ error: err?.response?.data?.message || err?.message || 'Import failed.', errors: err?.response?.data?.errors });
      setStep('result');
    }
  };

  if (!open) return null;

  const isSuccess = result && !result.error && result.status !== 'failed';
  const isError = result && (!!result.error || result.status === 'failed');
  const fileSize = file ? (file.size < 1024 ? `${file.size} B` : file.size < 1048576 ? `${(file.size / 1024).toFixed(1)} KB` : `${(file.size / 1048576).toFixed(1)} MB`) : '';

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="relative px-7 pt-7 pb-5">
          <button onClick={handleClose} className="absolute top-5 right-5 p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X size={18} />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#f08804] to-[#e88b00] flex items-center justify-center shadow-lg shadow-[#FFD89E]/50">
              <Upload size={22} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">{title}</h3>
              <p className="text-[13px] text-slate-400 mt-0.5">Supports Bizboard ERP exported Excel format</p>
            </div>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

        {/* Body */}
        <div className="px-7 py-6">

          {/* ── SELECT STEP ── */}
          {step === 'select' && (
            <div>
              <div
                className={`relative border-2 border-dashed rounded-2xl transition-all cursor-pointer ${
                  dragOver
                    ? 'border-[#f08804] bg-[#FFF4E6]/80'
                    : file
                      ? 'border-emerald-300 bg-emerald-50/30'
                      : 'border-slate-200 bg-slate-50/30 hover:border-[#FFD89E] hover:bg-[#FFF4E6]/20'
                }`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => !file && fileInputRef.current?.click()}
              >
                <input type="file" ref={fileInputRef} accept={acceptTypes} className="hidden" onChange={e => handleFileSelect(e.target.files?.[0])} />

                {file ? (
                  <div className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <FileSpreadsheet size={28} className="text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-800 truncate">{file.name}</p>
                        <p className="text-[12px] text-slate-400 mt-1">{fileSize} · {file.name.split('.').pop().toUpperCase()}</p>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); setFile(null); }}
                        className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <CheckCircle size={14} className="text-emerald-500" />
                      <span className="text-[12px] text-emerald-600 font-semibold">File ready for import</span>
                    </div>
                  </div>
                ) : (
                  <div className="py-12 px-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      {dragOver ? (
                        <Download size={28} className="text-[#f08804] animate-bounce" />
                      ) : (
                        <File size={28} className="text-slate-300" />
                      )}
                    </div>
                    <p className="text-sm font-semibold text-slate-700">
                      {dragOver ? 'Drop your file here' : 'Drag & drop your file here'}
                    </p>
                    <p className="text-[12px] text-slate-400 mt-2">or <span className="text-[#C45500] font-semibold cursor-pointer hover:underline">browse</span> to select</p>
                    <div className="flex items-center justify-center gap-2 mt-4">
                      {['XLSX', 'XLS', 'CSV'].map(ext => (
                        <span key={ext} className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2.5 py-1 rounded-md">.{ext}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {templateUrl && (
                <a href={templateUrl} download className="inline-flex items-center gap-1.5 mt-4 text-[12px] font-semibold text-[#C45500] hover:text-[#C45500] transition-colors">
                  <Download size={13} /> Download sample template
                </a>
              )}
            </div>
          )}

          {/* ── UPLOADING STEP ── */}
          {step === 'uploading' && (
            <div className="py-10 text-center">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                  <circle cx="40" cy="40" r="34" stroke="#6366f1" strokeWidth="6" fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 34}`}
                    strokeDashoffset={`${2 * Math.PI * 34 * (1 - Math.min(progress, 100) / 100)}`}
                    style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold text-[#C45500]">{Math.round(Math.min(progress, 100))}%</span>
                </div>
              </div>
              <p className="text-sm font-semibold text-slate-700">Processing your file...</p>
              <p className="text-[12px] text-slate-400 mt-1">This may take a moment depending on the file size</p>
            </div>
          )}

          {/* ── RESULT STEP ── */}
          {step === 'result' && result && (
            <div>
              <div className="flex flex-col items-center mb-6">
                {isSuccess ? (
                  <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4 ring-4 ring-emerald-50">
                    <CheckCircle size={32} className="text-emerald-600" />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mb-4 ring-4 ring-red-50">
                    <XCircle size={32} className="text-red-600" />
                  </div>
                )}
                <h3 className={`text-lg font-bold ${isSuccess ? 'text-emerald-700' : 'text-red-700'}`}>
                  {isSuccess ? 'Import Successful' : 'Import Failed'}
                </h3>
                <p className="text-sm text-slate-500 mt-1 text-center">{result.message || result.error || ''}</p>
              </div>

              {isSuccess && (
                <div className="bg-slate-50 rounded-xl overflow-hidden border border-slate-100">
                  {[
                    { label: 'Imported', value: result.successCount, color: 'text-emerald-600', show: result.successCount !== undefined },
                    { label: 'Failed', value: result.failureCount, color: 'text-red-600', show: result.failureCount > 0 },
                    { label: 'Skipped', value: result.skipCount, color: 'text-amber-600', show: result.skipCount > 0 },
                  ].filter(r => r.show).map((row, i) => (
                    <div key={i} className={`flex justify-between items-center px-5 py-3 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
                      <span className="text-sm text-slate-500">{row.label}</span>
                      <span className={`text-sm font-bold ${row.color}`}>{row.value}</span>
                    </div>
                  ))}
                  {Array.isArray(result.skippedUsers) && result.skippedUsers.length > 0 && (
                    <div className="border-t border-slate-100 px-5 py-3">
                      <p className="text-[12px] font-semibold text-slate-600 mb-2">Skipped items:</p>
                      <div className="max-h-28 overflow-y-auto space-y-1">
                        {result.skippedUsers.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-1.5 text-[12px] text-slate-500">
                            <AlertTriangle size={11} className="text-amber-500 flex-shrink-0" />{item}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isError && (
                <div className="bg-red-50 rounded-xl p-5 border border-red-100">
                  <p className="text-sm text-red-700 font-medium">{result.error || result.message}</p>
                  {Array.isArray(result.errors) && result.errors.length > 0 && (
                    <div className="mt-3 max-h-28 overflow-y-auto space-y-1">
                      {result.errors.map((err, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 text-[12px] text-red-600">
                          <XCircle size={11} className="flex-shrink-0" />{err}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-3">
          {step === 'select' && (
            <>
              <button onClick={handleClose} className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">Cancel</button>
              <button onClick={handleUpload} disabled={!file} className="px-6 py-2.5 text-sm font-semibold text-white bg-[#232F3E] hover:bg-[#37475A] rounded-xl transition-colors disabled:opacity-40 flex items-center gap-2 shadow-sm">
                <Upload size={15} /> Start Import
              </button>
            </>
          )}
          {step === 'result' && (
            <button onClick={handleClose} className="px-6 py-2.5 text-sm font-semibold text-white bg-[#232F3E] hover:bg-[#37475A] rounded-xl transition-colors shadow-sm">Close</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
